import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { get } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import type { FilterOptions } from "common/types";

import type { Conversation } from "../types";

async function getConversations(
  workspaceId: string,
  itemsPerPage: number,
  keywords?: string,
  selectedAccountIds?: string[],
  selectedCampaignIds?: string[],
  selectedLabelIds?: string[],
  timestamp?: number | "now",
  filteredBy?: FilterOptions,
  signal?: AbortSignal,
): Promise<Conversation[]> {
  const searchParams = new URLSearchParams();
  searchParams.append("count", itemsPerPage.toString());
  searchParams.append(
    "before",
    (timestamp === "now" ? Date.now() : timestamp).toString(),
  );

  if (keywords) {
    searchParams.append("keywords", keywords);
  }

  if (selectedAccountIds?.length) {
    searchParams.append("account_ids", JSON.stringify(selectedAccountIds));
  }

  if (selectedCampaignIds?.length) {
    searchParams.append("campaigns", JSON.stringify(selectedCampaignIds));
  }

  if (selectedLabelIds?.length) {
    searchParams.append("labels", JSON.stringify(selectedLabelIds));
  }

  if (filteredBy === "favorite") {
    searchParams.append("favorite", "true");
  }

  if (filteredBy === "unread") {
    searchParams.append("unread", "true");
  }

  const timeoutAbortSignal = AbortSignal.timeout(30000);

  return (
    await get(
      `workspaces/${workspaceId}/conversations?${searchParams.toString()}`,
      signal
        ? AbortSignal.any([signal, timeoutAbortSignal])
        : timeoutAbortSignal,
    )
  ).conversations;
}

const itemsPerPage = 35;

export default function useConversations({
  keywords,
  selectedAccountIds = [],
  selectedCampaignIds = [],
  selectedLabelIds = [],
  filteredBy = "all",
}: {
  keywords?: string;
  selectedAccountIds?: string[];
  selectedCampaignIds?: string[];
  selectedLabelIds?: string[];
  filteredBy?: FilterOptions;
} = {}) {
  //this is the abort signal used to abort the unread message fetch request when required
  const abortControllerRef = useRef<AbortController>();

  const { id: workspaceId, accounts_count: accountsCount } =
    useSelectedWorkspaceContext();
  const { conversationsKeys } = getQueryKeys(workspaceId);
  const queryClient = useQueryClient();

  const isFirstDataRef = useRef(true);
  const [conversations, setConversations] = useState<Conversation[]>();

  const queryKey = [
    ...conversationsKeys.list(),
    {
      keywords,
      selectedAccountIds,
      selectedCampaignIds,
      selectedLabelIds,
      filteredBy,
    },
  ];

  const {
    isFetching: isLoadingConversations,
    data: queryConversations,
    fetchNextPage: loadNextConversations,
    hasNextPage: hasMoreConversations,
    error: conversationsError,
    isRefetching,
    refetch: refetchConversations,
  } = useInfiniteQuery<Conversation[], number | "now">({
    queryKey,
    queryFn: ({ pageParam }) =>
      getConversations(
        workspaceId,
        itemsPerPage,
        keywords,
        selectedAccountIds,
        selectedCampaignIds,
        selectedLabelIds,
        pageParam as number | "now",
        filteredBy,
      ),
    refetchOnMount: false,
    gcTime: 0,
    initialPageParam: "now",
    enabled: accountsCount > 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.length === itemsPerPage) {
        return lastPage[lastPage.length - 1].last_message.created_at;
      }
      return undefined;
    },
  });

  useEffect(() => {
    isFirstDataRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, [
    keywords,
    selectedAccountIds,
    selectedCampaignIds,
    selectedLabelIds,
    filteredBy,
  ]);

  function getUpdatedAndMergedConversations(newConversations: Conversation[]) {
    const updatedConversations = conversations.map((originalConversation) => {
      const updatedConversationIndex = newConversations.findIndex(
        ({ conversation_urn }) =>
          conversation_urn === originalConversation.conversation_urn,
      );
      if (updatedConversationIndex !== -1) {
        return newConversations.splice(updatedConversationIndex, 1)[0];
      }
      return originalConversation;
    });
    return [...updatedConversations, ...newConversations].sort(
      (a, b) =>
        new Date(b.last_activity_at).getTime() -
        new Date(a.last_activity_at).getTime(),
    );
  }

  useEffect(() => {
    if (!isFirstDataRef.current && queryConversations) {
      const flatConversations = queryConversations.pages.flat();
      setConversations(getUpdatedAndMergedConversations(flatConversations));
    } else {
      setConversations(queryConversations?.pages.flat());

      if (queryConversations && !isRefetching) {
        isFirstDataRef.current = false;
      }
    }
  }, [queryConversations, isRefetching]);

  async function onUnreadMessage() {
    abortControllerRef.current = new AbortController();

    await getConversations(
      workspaceId,
      10,
      keywords,
      selectedAccountIds,
      selectedCampaignIds,
      selectedLabelIds,
      "now",
      filteredBy,
      abortControllerRef.current.signal,
    ).then((newConversations) => {
      const updatedConversations =
        getUpdatedAndMergedConversations(newConversations);

      setConversations(updatedConversations);

      queryClient.setQueryData(queryKey, () => {
        const pageParams: (number | "now")[] = ["now"];
        const pages: Conversation[][] = [];
        for (
          let i = 0;
          i < Math.ceil(updatedConversations.length / itemsPerPage);
          i += 1
        ) {
          const pageContent = [];
          for (let j = 0; j < itemsPerPage; j += 1) {
            const index = j + itemsPerPage * i;
            if (updatedConversations[index]) {
              pageContent.push(updatedConversations[index]);
            }
          }
          pages.push(pageContent);
          pageParams.push(
            pageContent[pageContent.length - 1].last_message.created_at,
          );
        }
        return { pages, pageParams };
      });
    });
  }

  return {
    conversations,
    isLoadingConversations: isLoadingConversations,
    loadNextConversations,
    hasMoreConversations,
    conversationsError,
    refetchConversations,
    onUnreadMessage,
  };
}
